import { useMemo } from "react";
import type { TooltipProps } from "recharts";
import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import type { Payload as TooltipPayload } from "recharts/types/component/DefaultTooltipContent";
import {
    getSystemFillColor,
    OFFSET_CURRENT_COLOR_ENOVA_ACCUMULATED_CHART,
    OFFSET_PREVIOUS_COLOR_ENOVA_ACCUMULATED_CHART,
} from "../../colors";
import {
    AreaSeparator,
    ChartDataTable,
    CustomLegend,
    LegendColorIcon,
    TooltipCard,
    TooltipItem,
    XAxisTick,
    YAxisTick,
} from "../../components";
import { TOOLTIP_OFFSET } from "../chart-constants";
import type { EnovaActivityPoint } from "../enova-activity-utils";

const SUPPORT_UNIT = "stk.";
const PREVIOUS_LABEL = "Støttet tidligere år";
const CURRENT_LABEL = "Støttet dette året";
const DEFAULT_SYSTEM_KEY = "Godstransport";

type AccumulatedSupportPoint = {
    year: number;
    previousSupport: number;
    currentSupport: number;
    totalSupport: number;
};

const formatInteger = (value: number | string) => {
    const numericValue = typeof value === "number" ? value : Number(value);
    if (!Number.isFinite(numericValue)) {
        return "0";
    }
    return numericValue.toLocaleString("nb-NO");
};

type AccumulatedTooltipProps = TooltipProps<number, string> & {
    payload?: TooltipPayload<number, string>[];
};

const EnovaAccumulatedTooltip: React.FC<AccumulatedTooltipProps> = ({ active, payload = [] }) => {
    if (!active || payload.length === 0) {
        return null;
    }

    const year = payload[0]?.payload?.year;
    const totalSupport = payload[0]?.payload?.totalSupport ?? 0;
    const previousEntry = payload.find((entry) => entry?.dataKey === "previousSupport");
    const currentEntry = payload.find((entry) => entry?.dataKey === "currentSupport");
    const previousValue = typeof previousEntry?.value === "number" ? previousEntry.value : 0;
    const currentValue = typeof currentEntry?.value === "number" ? currentEntry.value : 0;

    return (
        <TooltipCard title={year}>
            <TooltipItem label={CURRENT_LABEL} value={currentValue} unit={SUPPORT_UNIT} formatValue={formatInteger} />
            <AreaSeparator />

            {previousValue > 0 && (
                <>
                    <TooltipItem
                        label={PREVIOUS_LABEL}
                        value={previousValue}
                        unit={SUPPORT_UNIT}
                        formatValue={formatInteger}
                    />
                    <AreaSeparator />
                </>
            )}

            <TooltipItem label="Totalt støttet" value={totalSupport} unit={SUPPORT_UNIT} formatValue={formatInteger} />
        </TooltipCard>
    );
};

interface EnovaAccumulatedChartProps {
    legendHeading: string;
    systemKey?: string | null;
    data: EnovaActivityPoint[];
}

const buildAccumulatedSupportData = (data: EnovaActivityPoint[]): AccumulatedSupportPoint[] => {
    let accumulated = 0;
    return data
        .slice()
        .sort((a, b) => a.year - b.year)
        .map(({ year, count }) => {
            const previousSupport = accumulated;
            const currentSupport = Number.isFinite(count) ? count : 0;
            const totalSupport = previousSupport + currentSupport;
            accumulated = totalSupport;
            return { year, previousSupport, currentSupport, totalSupport };
        });
};

export const EnovaAccumulatedChart: React.FC<EnovaAccumulatedChartProps> = ({ legendHeading, systemKey, data }) => {
    const resolvedSystemKey = systemKey ?? DEFAULT_SYSTEM_KEY;
    const previousColor = getSystemFillColor(resolvedSystemKey, OFFSET_PREVIOUS_COLOR_ENOVA_ACCUMULATED_CHART);
    const currentColor = getSystemFillColor(resolvedSystemKey, OFFSET_CURRENT_COLOR_ENOVA_ACCUMULATED_CHART);
    const accumulatedSupportData = useMemo(() => buildAccumulatedSupportData(data), [data]);

    const dataTableColumns = [
        { key: "year", label: "År", getValue: (row: AccumulatedSupportPoint) => String(row.year) },
        {
            key: "currentSupport",
            label: CURRENT_LABEL,
            getValue: (row: AccumulatedSupportPoint) => formatInteger(row.currentSupport),
        },
        {
            key: "previousSupport",
            label: PREVIOUS_LABEL,
            getValue: (row: AccumulatedSupportPoint) => formatInteger(row.previousSupport),
        },
        {
            key: "totalSupport",
            label: "Totalt støttet",
            getValue: (row: AccumulatedSupportPoint) => formatInteger(row.totalSupport),
        },
    ];

    return (
        <>
            <ChartDataTable
                caption={legendHeading}
                columns={dataTableColumns}
                rowKey={(row) => String(row.year)}
                rows={accumulatedSupportData}
            />
            <ResponsiveContainer width="100%" height={600}>
                <BarChart data={accumulatedSupportData} margin={{ top: 0, right: 30, left: 0, bottom: 20 }}>
                    <CartesianGrid strokeDasharray="1 1" vertical={false} syncWithTicks />

                    <XAxis
                        dataKey="year"
                        tick={<XAxisTick />}
                        interval={0}
                        label={{
                            value: "År",
                            position: { x: 850, y: 4 },
                            fill: "var(--ds-color-neutral-text-default)",
                            fontSize: "var(--ds-font-size-1)",
                            fontWeight: 600,
                        }}
                    />

                    <YAxis
                        tick={<YAxisTick formatValue={formatInteger} />}
                        allowDecimals={false}
                        label={{
                            value: "Antall støttet",
                            position: { x: 99, y: -10 },
                            fill: "var(--ds-color-neutral-text-default)",
                            fontSize: "var(--ds-font-size-1)",
                            fontWeight: 600,
                        }}
                        padding={{ top: 20, bottom: 0 }}
                    />

                    <Tooltip content={<EnovaAccumulatedTooltip />} offset={TOOLTIP_OFFSET} />

                    <Legend
                        verticalAlign="top"
                        wrapperStyle={{ paddingBottom: "40px" }}
                        content={
                            <CustomLegend heading={legendHeading}>
                                <LegendColorIcon label={CURRENT_LABEL} color={currentColor} />
                                <LegendColorIcon label={PREVIOUS_LABEL} color={previousColor} />
                            </CustomLegend>
                        }
                    />

                    <Bar
                        dataKey="previousSupport"
                        stackId="support"
                        fill={previousColor}
                        name={PREVIOUS_LABEL}
                        maxBarSize={64}
                    />
                    <Bar
                        dataKey="currentSupport"
                        stackId="support"
                        fill={currentColor}
                        name={CURRENT_LABEL}
                        maxBarSize={64}
                    />
                </BarChart>
            </ResponsiveContainer>
        </>
    );
};
